import type {
  ChatMessage,
  ChatMessageModelImagePart,
  ChatMessagePart,
  ChatMessageTextPart,
  MessagesResponse,
  RequestConfig,
  RequestInstruction,
  TextPartPurpose,
  UserMessage,
} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import type {AiPromptBoxAttachments} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {getAspectRatioFromDimensions} from '@Components/ai-chat-window/lib/chat-attachment-image.library';

/**
 * Builds a user message that replaces the content of an existing message (e.g. after edit).
 */
export const buildUserMessageReplacement = (messageId: string, parts: ChatMessagePart[]): UserMessage => ({
  role: MessageRole.USER,
  parts,
  messageId,
});

export const convertMessagesArrayToHashmap = (messages: ChatMessage[]): Record<string, ChatMessage> => {
  if (!messages || messages.length === 0) {
    return {};
  }

  return messages.reduce<Record<string, ChatMessage>>((acc, message, index) => {
    const messageId = message.messageId ?? index.toString();

    acc[messageId] = message;

    return acc;
  }, {});
};

export const getAiChatCompletedStateForInProgressChat = async (chatId: string, messageSessionUid: string): Promise<MessagesResponse> => {
  return (await window.PMW.pollingAjaxCallAsync(
    async (): Promise<void> => {},
    async (): Promise<MessagesResponse> => {
      return window.PMW.readLocal('ai/getAiChatCompletedState', {
        chatId: chatId,
        messageSessionUid: messageSessionUid,
      });
    }
  )) as MessagesResponse;
};

const shouldIncludeMessage = (message: ChatMessage): boolean => {
  if (message.role === MessageRole.DEVELOPER) {
    return 'failureType' in message;
  }

  return true;
};

export const groupMessagesIntoConversations = (messages: Record<string, ChatMessage>): ChatMessage[][] => {
  const conversations: ChatMessage[][] = [];
  let currentConversation: ChatMessage[] = [];

  const orderedMessages = Object.values(messages).filter(shouldIncludeMessage);

  orderedMessages.forEach((message) => {
    if (message.role === MessageRole.USER) {
      if (currentConversation.length > 0) {
        conversations.push(currentConversation);
      }
      currentConversation = [message];
    } else {
      currentConversation.push(message);
    }
  });

  if (currentConversation.length > 0) {
    conversations.push(currentConversation);
  }

  return conversations;
};

export const findLastUserMessage = (messages: Record<string, ChatMessage>): ChatMessage | undefined => {
  const messageValues = Object.values(messages);

  return messageValues.reverse().find((msg) => msg.role === MessageRole.USER);
};

export const findLastImagePart = (messages: Record<string, ChatMessage>): ChatMessageModelImagePart | undefined => {
  const messageValues = Object.values(messages);

  for (let i = messageValues.length - 1; i >= 0; i--) {
    const message = messageValues[i];
    if (message.parts) {
      for (let j = message.parts.length - 1; j >= 0; j--) {
        const part = message.parts[j];
        if (part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE && 'width' in part && 'height' in part) {
          return part;
        }
      }
    }
  }

  return undefined;
};

export const getAspectRatioForLastGeneratedImage = (messages: Record<string, ChatMessage>): string | undefined => {
  const lastImagePart = findLastImagePart(messages);

  if (lastImagePart?.width && lastImagePart?.height) {
    return getAspectRatioFromDimensions(lastImagePart.width, lastImagePart.height);
  }

  return undefined;
};

export const buildRequestConfig = (messages: Record<string, ChatMessage>, existingRequestConfig?: RequestConfig, requestInstruction?: RequestInstruction): RequestConfig | undefined => {
  let config = existingRequestConfig;

  if (!config?.aspectRatio) {
    const aspectRatio = getAspectRatioForLastGeneratedImage(messages);
    if (aspectRatio) {
      config = {...config, aspectRatio};
    }
  }

  if (requestInstruction) {
    config = {...config, requestInstruction};
  }

  return config;
};

export const convertAttachmentsToUserRequestParts = (attachments: AiPromptBoxAttachments['items']): ChatMessagePart[] => {
  const userRequestParts: ChatMessagePart[] = [];

  attachments.forEach((attachment) => {
    switch (attachment.type) {
      case PromptAttachmentType.IMAGE:
      case PromptAttachmentType.CAPTURE_PHOTO:
        userRequestParts.push({
          type: ChatMessagePartType.USER_IMAGE,
          hashedFilename: attachment.hashedFilename,
          source: attachment.source,
          extension: attachment.extension,
        });
        break;
      case PromptAttachmentType.DESIGN:
      case PromptAttachmentType.DESIGN_TEMPLATE:
        userRequestParts.push({
          type: ChatMessagePartType.DESIGN,
          hashedId: attachment.designId,
          pageHashedId: attachment.pageHashedId,
        });
        break;
      case PromptAttachmentType.EXAMPLE_IMAGE:
        userRequestParts.push({
          type: ChatMessagePartType.EXAMPLE_IMAGE_ASSET,
          fileName: attachment.fileName,
        });
        break;
      case PromptAttachmentType.REATTACHED_MODEL_IMAGE:
        userRequestParts.push({
          type: ChatMessagePartType.REATTACHED_MODEL_IMAGE,
          hashedId: attachment.imageHashedId,
        });
        break;
    }
  });

  return userRequestParts;
};

export const createUserRequestParts = (prompt: string, attachments: AiPromptBoxAttachments['items'], purpose?: TextPartPurpose): ChatMessagePart[] => {
  const attachmentsUserRequestParts = convertAttachmentsToUserRequestParts(attachments);
  const textPart: ChatMessageTextPart = {type: ChatMessagePartType.TEXT, text: prompt};
  if (purpose) {
    textPart.purpose = purpose;
  }
  return [textPart, ...attachmentsUserRequestParts];
};
